import {
    DatasetGeometryPoint,
    FilterItem,
    FilterObject,
    LogicOperator,
} from "@biggeo/bg-server-lib/studio";
import { useDataGridOptions } from "@biggeo/bg-ui/lab";
import { isSuccess, map2, withDefault } from "@vividtheory/remotedata";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import isEmpty from "lodash/isEmpty";
import uniqBy from "lodash/uniqBy";
import { useMemo, useState } from "react";
import { match } from "ts-pattern";
import { v4 as uuid } from "uuid";

import { toNonReadonlyArray } from "@biggeo/bg-utils";
import compact from "lodash/compact";
import { ColorSwatchOption } from "../../../common/components/ColorSwatchSelector";
import { AuroraClient } from "../../../utils/socket";
import { AuroraError } from "../../../velocity/aurora/common";
import { OrderByOptions as AuroraOrderByOptions } from "../../../velocity/aurora/filter";
import {
    DataRspSuccess,
    SchemaRow,
    SchemaRspSuccess,
} from "../../../velocity/aurora/map";
import {
    useAuroraDataQuery,
    useAuroraSchemaQuery,
} from "../../hooks/aurora-hooks";
import { MapTableTabs } from "../../map-wrappers/MapViewWrapper";
import {
    GeospatialSelection,
    MapContextDataset,
} from "../../mapbox/context/types";
import { getFilteredDataInput } from "../../mapbox/utils/filtered-data-hooks-utils";
import { getDatasetShapeColor } from "../../utils/style-utils";
import { DataRspRows } from "../../utils/type";
import { DatasetTable } from "../components/DatasetTable";
import { FILTERED_DATA_TABLE_LIMIT } from "../utils/utils";
import { getFilterObjectFromDataset } from "../../mapbox/context/utils/map-utils";

export enum DatasetTableTab {
    viewport = "viewport",
    all = "all",
}

interface DatasetTableContainerProps {
    readonly data: MapContextDataset;
    readonly channelId: string;
    readonly geospatialSelection: GeospatialSelection;
    readonly filterId?: string;
    readonly updateDataset?: (
        i: Partial<{
            filters: FilterItem[];
            logicOperator: LogicOperator;
            selectedRows: Omit<DatasetGeometryPoint, "fkDataSourceId">[];
            pinnedRows: Omit<DatasetGeometryPoint, "fkDataSourceId">[];
        }>
    ) => void;
    readonly onGoToClick: (rows: DataRspRows) => void;
    readonly heatmap?: ColorSwatchOption;
    readonly mainTab: MapTableTabs;
    readonly tableId: string;
    readonly client?: AuroraClient;
    readonly hasTransitioned: boolean;
}

const DatasetTableContainer = ({
    channelId,
    data,
    filterId,
    updateDataset,
    onGoToClick,
    heatmap,
    mainTab,
    tableId,
    client,
    hasTransitioned,
    ...props
}: DatasetTableContainerProps) => {
    const [view, setView] = useState(DatasetTableTab.viewport);

    const geospatialSelection = useMemo(
        () =>
            match<DatasetTableTab, GeospatialSelection | undefined>(view)
                .with(DatasetTableTab.viewport, () => props.geospatialSelection)
                .with(DatasetTableTab.all, () => undefined)
                .exhaustive(),
        [view, props.geospatialSelection]
    );

    const filter: FilterObject = getFilterObjectFromDataset(data);

    const {
        dataGridFetchInputProps: filteredDataGridProps,
        filterSearchPaginateProps: filteredDataPaginationProps,
    } = useDataGridOptions(
        FILTERED_DATA_TABLE_LIMIT,
        data.dataSource.tableId || undefined
    );

    const styles = getDatasetShapeColor(data);

    const initialFilters = {
        logicOperator: filter.logicOperator,
        filters: pipe(
            filter.filters || [],
            toNonReadonlyArray,
            A.map((f) => {
                return {
                    id: uuid(),
                    filterId,
                    column: f.column,
                    operator: f.operator,
                    type: f.type,
                    data: f.data,
                };
            })
        ),
    };

    const sortModel = filteredDataGridProps.sortModel[0];

    const input = getFilteredDataInput({
        geospatialSelection,
        fkOrganizationId: data.dataSource.fkOrganizationId,
        fkDataSourceId: data.dataSource.id,
        filter,
        limit: filteredDataGridProps.pageLimit,
        offset:
            filteredDataGridProps.pageLimit *
            (filteredDataGridProps.pageIndex - 1),
        orderBy: sortModel
            ? {
                  column: sortModel.field,
                  order: match(sortModel.sort)
                      .with("asc", () => AuroraOrderByOptions.ASC)
                      .with("desc", () => AuroraOrderByOptions.DESC)
                      .otherwise(() => AuroraOrderByOptions.DESC),
              }
            : undefined,
    });

    input.geographyColumn = data.dataSource.geographyColumn ?? "";

    /**
     * For Split tab, only fetch when hasTransitioned is true. This means that the height
     * is done transitioning (the table is now fully visible) and the viewport won't change again unless we move the map.
     */
    const { dataRemoteData } = useAuroraDataQuery(
        input,
        filter.databaseId,
        client,
        hasTransitioned
            ? data.dataSourceConfiguration.serverUrl || undefined
            : undefined
    );

    const { schemaRemoteData } = useAuroraSchemaQuery(
        {
            datasetId: data.dataSource.id,
            organizationId: data.dataSource.fkOrganizationId,
            collection: data.dataSource.collectionName ?? "",
        },
        client,
        data.dataSourceConfiguration.serverUrl || undefined
    );

    const combinedRemoteData = map2(
        (c: SchemaRspSuccess) => (d: DataRspSuccess) => ({
            columns: c.rows,
            rowsCountConnection: {
                count: d.count,
                rows: d.rows,
            },
        }),
        schemaRemoteData,
        dataRemoteData
    );
    const remoteData = pipe(
        withDefault<
            AuroraError,
            { columns: SchemaRow[]; rowsCountConnection: DataRspSuccess }
        >(
            {
                columns: [],
                rowsCountConnection: { count: 0, rows: [] },
            },
            combinedRemoteData
        ),
        (result) =>
            tableId
                ? {
                      ...result,
                      rowsCountConnection: {
                          ...result.rowsCountConnection,
                          rows: uniqBy(
                              result.rowsCountConnection.rows,
                              tableId
                          ),
                      },
                  }
                : result
    );

    return (
        <DatasetTable
            view={view}
            setView={setView}
            data={remoteData.rowsCountConnection}
            columns={remoteData.columns}
            type={"data"}
            heatmap={styles ? styles.heatmap : heatmap}
            loading={!isSuccess(combinedRemoteData)}
            dataPaginationProps={filteredDataPaginationProps}
            initialFilters={initialFilters}
            updateDataset={updateDataset}
            onGoToClick={onGoToClick}
            isGoToFieldHidden={mainTab === MapTableTabs.table}
            tableId={tableId}
            isFocused={
                !isEmpty(data.selectedRows)
                    ? !data.configuration.showPoints
                    : false
            }
            selectedRows={pipe(
                data.selectedRows,
                A.map((r) => (r.row ? JSON.parse(r.row) : undefined)),
                compact
            )}
            pinnedRows={pipe(
                data.pinnedRows,
                A.map((r) => (r.row ? JSON.parse(r.row) : undefined)),
                compact
            )}
        />
    );
};

export default DatasetTableContainer;
